import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Mail, Send, Layers, MoonStar, Sun, Moon, Smartphone, Monitor } from 'lucide-react';
import { PREVIEW_MODES, simulateDarkMode } from '@/utils/emailDarkModeSim';

// Sep 2026 — Live-Vorschau aller Transaktions-Mail-Templates + Testversand (einzeln, Gruppe, alle)
export default function EmailPreviewAdminPanel() {
  const [previews, setPreviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // 'one' | 'group' | 'all' | null
  const [testEmail, setTestEmail] = useState('');
  const [mode, setMode] = useState('light');
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'mobile' (375 px)

  useEffect(() => {
    api.get('/admin/email-previews')
      .then((r) => {
        setPreviews(r.data.previews || []);
        setSelected((r.data.previews || [])[0] || null);
      })
      .catch(() => toast.error('Vorschau konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-gray-500 py-8">Lade E-Mail-Vorschauen…</p>;

  const groups = [...new Set(previews.map((p) => p.group))];
  const body = () => (testEmail.trim() ? { to_email: testEmail.trim() } : {});

  const sendOne = async () => {
    setSending('one');
    try {
      const r = await api.post(`/admin/email-previews/${selected.key}/send`, body());
      toast.success(`Testversand an ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSending(null);
    }
  };

  const sendMany = async (group) => {
    const count = group ? previews.filter((p) => p.group === group).length : previews.length;
    const target = testEmail.trim() || 'deine Admin-Adresse';
    if (!window.confirm(`${count} Vorlagen an ${target} senden?`)) return;
    setSending(group ? 'group' : 'all');
    try {
      const r = await api.post('/admin/email-previews/send-all', { ...body(), ...(group ? { group } : {}) });
      const failed = r.data.failed?.length ? ` · ${r.data.failed.length} fehlgeschlagen` : '';
      toast.success(`${r.data.sent}/${r.data.total} Vorlagen an ${r.data.sent_to} gesendet${failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-email-preview-panel">
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid="email-preview-bulk-bar">
        <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
          <MoonStar className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="font-medium">Dark-Mode-Check:</span>
          <span className="text-gray-500 truncate">alle Vorlagen an einen echten Client schicken</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:ml-auto">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Testadresse (leer = an mich)"
            data-testid="email-preview-test-address"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            type="button"
            data-testid="email-preview-send-all-btn"
            disabled={!!sending || !previews.length}
            onClick={() => sendMany(null)}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Layers className="h-3.5 w-3.5" />
            {sending === 'all' ? 'Sendet…' : `Alle ${previews.length} senden`}
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-72 shrink-0 space-y-4">
          {groups.map((g) => (
            <div key={g}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{g}</p>
                <button
                  type="button"
                  data-testid={`email-preview-send-group-${g.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  disabled={!!sending}
                  onClick={() => sendMany(g)}
                  title={`Alle Vorlagen der Gruppe „${g}" senden`}
                  className="text-[11px] text-emerald-700 hover:underline disabled:opacity-50"
                >
                  {sending === 'group' ? '…' : 'Gruppe senden'}
                </button>
              </div>
              <div className="space-y-1">
                {previews.filter((p) => p.group === g).map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`email-preview-item-${p.key}`}
                    onClick={() => setSelected(p)}
                    className={`w-full flex items-center gap-2 text-left text-sm rounded-lg px-3 py-2 transition-colors ${
                      selected?.key === p.key
                        ? 'bg-emerald-50 text-emerald-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          {selected && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">Betreff</p>
                  <p className="text-sm font-medium text-gray-800 truncate" data-testid="email-preview-subject">{selected.subject}</p>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="tablist" aria-label="Gerät" data-testid="email-preview-device-toggle">
                    {[{ id: 'desktop', label: 'Desktop', Icon: Monitor }, { id: 'mobile', label: 'Handy 375 px', Icon: Smartphone }].map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        role="tab"
                        aria-selected={device === d.id}
                        data-testid={`email-preview-device-${d.id}`}
                        onClick={() => setDevice(d.id)}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          device === d.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        <d.Icon className="h-3 w-3" />
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="tablist" aria-label="Darstellungsmodus" data-testid="email-preview-mode-toggle">
                    {PREVIEW_MODES.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        role="tab"
                        aria-selected={mode === m.id}
                        data-testid={`email-preview-mode-${m.id}`}
                        onClick={() => setMode(m.id)}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          mode === m.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        {m.id === 'light' ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid="email-preview-send-btn"
                    disabled={!!sending}
                    onClick={sendOne}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending === 'one' ? 'Sendet…' : (testEmail.trim() ? 'An Testadresse senden' : 'An mich senden')}
                  </button>
                </div>
              </div>
              {mode !== 'light' && (
                <p className="bg-gray-900 text-gray-300 text-[11px] px-4 py-1.5" data-testid="email-preview-mode-hint">
                  Simulation: {mode === 'apple' ? 'prefers-color-scheme: dark (Apple Mail, iOS Mail, Outlook macOS)' : 'Outlook-Farbumkehr mit data-ogsc/data-ogsb (Outlook.com, Windows, Android)'} — echte Clients können abweichen, zur Sicherheit „An Testadresse senden".
                </p>
              )}
              <div className={`${mode === 'light' ? 'bg-gray-100' : 'bg-black'} ${device === 'mobile' ? 'py-5 flex justify-center' : ''}`} data-testid="email-preview-stage">
                <iframe
                  key={`${mode}-${device}`}
                  title="E-Mail-Vorschau"
                  srcDoc={simulateDarkMode(selected.html, mode)}
                  sandbox=""
                  className={`${mode === 'light' ? 'bg-white' : 'bg-black'} ${device === 'mobile' ? 'rounded-[28px] border-[8px] border-gray-900 shadow-xl' : 'w-full'}`}
                  style={device === 'mobile' ? { width: 375 + 16, height: 'min(812px, 70vh)' } : { height: '70vh', border: 'none' }}
                  data-testid="email-preview-iframe"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
